import type { ListPhase } from './listState.js';

/**
 * The four states, for a `<select>`.
 *
 * A dropdown is where the collapse is least visible and most expensive. The
 * shape across this app is:
 *
 *     &lt;select&gt;
 *       &lt;option value=""&gt;Any program&lt;/option&gt;
 *       {(q.data?.programs ?? []).map(…)}
 *     &lt;/select&gt;
 *
 * with no loading branch, no error branch and no refusal branch — so a 403 on
 * `GET /programs`, a 500, a request still in flight, a program-scoped grant
 * that returned two of nine, and an organization that genuinely has no programs
 * all render as the identical control: one option, apparently usable, offering
 * nothing. The reader concludes their organization has no programs. Worse, the
 * control still LOOKS operable, which is the specific defect this codebase
 * ranks worst — and when the picker is a required field, the submit button goes
 * quietly dead beside it with nothing on screen to explain why.
 *
 * The list itself is never fabricated here. What this decides is the placeholder
 * option, whether the control should be inert, and the sentence beside it.
 */
export interface PickerState {
  /**
   * True when there is nothing to choose and asking again will not help. An
   * inert control is honest; an operable one that cannot be operated is not.
   */
  disabled: boolean;
  /** The text of the leading `<option>` — never the caller's "Any …" when there is no list. */
  placeholder: string;
  /**
   * The sentence beside the control, or null when the list is a real list and
   * needs no defending.
   */
  note: string | null;
}

/**
 * @param phase the same `listPhase` every panel uses, so a picker and the list
 *              it filters cannot disagree about what happened
 * @param what  plural noun for the things being chosen — "programs",
 *              "credential types", "people"
 * @param anyLabel the caller's own leading option ("Any program"), used only
 *              when there is genuinely something to choose from
 */
export function pickerState(phase: ListPhase, what: string, anyLabel: string): PickerState {
  switch (phase.kind) {
    case 'loading':
      // Not "no programs" — not answered yet. The control is inert because a
      // choice made against a list that has not arrived is a choice made
      // against nothing.
      return { disabled: true, placeholder: `Loading ${what}…`, note: null };
    case 'refused':
      return {
        disabled: true,
        placeholder: 'Not shown to you',
        note: `You may not see the ${what} in this organization, so there is nothing here to choose from. The list is withheld, not empty.`,
      };
    case 'failed':
      return {
        disabled: true,
        placeholder: `Couldn’t load ${what}`,
        note: `The ${what} did not load. This list is empty because of that, not because your organization has none.`,
      };
    case 'empty':
      // The one branch entitled to make a claim about the organization.
      return { disabled: true, placeholder: `No ${what} yet`, note: null };
    case 'stale':
      return phase.retainedEmpty
        ? { disabled: true, placeholder: `No ${what} yet`, note: null }
        : { disabled: false, placeholder: anyLabel, note: null };
    case 'ready':
      return { disabled: false, placeholder: anyLabel, note: null };
  }
}
